<template>
  <div class="flex items-center gap-1.5">
    <div class="glass-tile flex min-w-0 flex-1 flex-wrap items-center gap-2 rounded-xl p-2">
      <ui-popover :open="fieldMenuOpen" @update:open="(value) => onFieldMenuOpen(value)">
        <ui-popover-trigger as-child>
          <button
            type="button"
            data-slot="condition-field"
            :disabled="disabled"
            class="glass-input flex h-9 w-56 items-center justify-between gap-2 rounded-md px-3 text-left text-sm focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span
              v-if="selectedField"
              v-tooltip.right="selectedField.tooltip || null"
              class="flex min-w-0 items-center gap-1.5"
            >
              <span class="truncate">{{ tr(selectedField.label) }}</span>
              <triangle-alert-icon v-if="selectedField.disabled" class="size-4 shrink-0 text-destructive" />
            </span>
            <span v-else class="truncate text-muted-foreground">{{ tr('Select filter options') }}</span>
            <chevrons-up-down-icon class="size-4 shrink-0 opacity-50" />
          </button>
        </ui-popover-trigger>
        <ui-popover-content align="start" class="flex w-96 flex-col gap-1 p-1">
          <ui-input
            v-model="fieldSearch"
            class="h-8"
            :placeholder="tr('Search') + '...'"
          />
          <div class="max-h-72 overflow-y-auto">
            <button
              v-for="(field, index) in visibleFieldOptions"
              :key="'condition-field-' + index"
              v-tooltip.left="field.tooltip || null"
              type="button"
              data-slot="condition-field-option"
              :class="[
                'flex w-full cursor-pointer items-center gap-1 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                field.disabled ? 'opacity-50' : '',
                field.isSubfieldOption ? 'pl-5' : '',
                !field.isSubfieldOption && field.machine_name === modelValue.field ? 'font-semibold' : '',
              ]"
              @click="() => selectField(field)"
            >
              <template v-if="field.isSubfieldOption">
                <span class="text-xs text-muted-foreground">{{ tr(field.parentLabel) }}</span>
                <span class="text-muted-foreground">›</span>
              </template>
              {{ tr(field.label) }}
            </button>
            <p v-if="visibleFieldOptions.length === 0" class="m-0 px-2 py-4 text-center text-sm text-muted-foreground">
              {{ tr('No results') }}
            </p>
          </div>
        </ui-popover-content>
      </ui-popover>

      <select-field
        v-if="showSubfields"
        v-model="modelValue.subfield"
        trigger-class="w-56"
        :options="currentSubfields"
        :reduce="(obj) => obj.machine_name"
        :placeholder="loadingSubfields ? tr('Loading...') : tr('Select an action type')"
        :disabled="disabled"
        :loading="loadingSubfields"
        searchable
      />

      <select-field
        v-if="showDatePeriods"
        v-model="modelValue.date_period"
        trigger-class="w-44"
        :options="datePeriods"
        :reduce="(obj) => obj.machine_name"
        :aria-label="tr('Period')"
        :disabled="disabled"
      />

      <select-field
        v-if="!isDataTypeLanguage"
        v-model="modelValue.comparison"
        trigger-class="w-48"
        :options="operatorOptions"
        :reduce="(obj) => obj.machine_name"
        :placeholder="tr('Select conditional rule')"
        :disabled="disabled || disabledComparisonSelect"
      />
      <select-field
        v-else
        v-model="modelValue.value"
        trigger-class="w-56"
        :options="languageOptions"
        label="name"
        :reduce="(obj) => obj.iso_639_1"
        :aria-label="tr('Language')"
        :disabled="disabled"
        searchable
        @update:model-value="setLanguageComparison()"
      />

      <!-- The dynamic operator's three controls wrap one by one with the row (contents), instead of squeezing into one cell. -->
      <div :class="isDynamicOperator ? 'contents' : 'flex min-w-48 flex-1 items-center gap-2'">
        <template v-if="isDynamicOperator">
          <ui-input
            v-model="modelValue.value"
            type="number"
            step="1"
            min="1"
            max="100"
            class="w-16 shrink-0"
            :aria-label="tr('Value')"
            :disabled="disabled || disabledValueInput"
          />
          <select-field
            v-model="modelValue.settings.dynamic_operator_direction"
            trigger-class="w-fit max-w-full shrink-0"
            :options="dynamicOperatorValueOptions"
            :reduce="(obj) => obj.machine_name"
            :disabled="disabled || disabledValueInput"
          />
          <select-field
            v-model="modelValue.settings.dynamic_operator_area"
            trigger-class="w-fit max-w-full shrink-0"
            :options="dynamicOperatorAreas"
            :reduce="(obj) => obj.machine_name"
            :disabled="disabled || disabledValueInput"
          />
        </template>
        <date-time-input
          v-else-if="isDataTypeDateTime"
          v-model="modelValue.value"
          :disabled="disabled || disabledValueInput"
        />
        <ui-input
          v-else-if="isDataTypeString"
          v-model="modelValue.value"
          :placeholder="tr('Set filter text')"
          :disabled="disabled || disabledValueInput"
        />
        <number-input
          v-else-if="isDataTypeInt"
          v-model="modelValue.value"
          :step="1"
          :min="0"
          :placeholder="tr('Set number on filter (days etc.)')"
          :disabled="disabled || disabledValueInput"
        />
        <number-input
          v-else-if="isDataTypeFloat"
          v-model="modelValue.value"
          :step="0.001"
          :min="0"
          :placeholder="tr('Set currency on filter')"
          :disabled="disabled || disabledValueInput"
        />
        <money-input
          v-else-if="isDataTypeMoney"
          v-model="modelValue.value"
          class="w-full"
          :disabled="disabled || disabledValueInput"
          :currency="adAccount.currencyDetails ? adAccount.currencyDetails.code : 'USD'"
        />
        <percentage-input
          v-else-if="isDataTypePercentage"
          v-model="modelValue.value"
          class="w-28"
          :decimals="2"
          :disabled="disabled || disabledValueInput"
        />
        <ui-input
          v-else-if="!isDataTypeLanguage"
          disabled
          :placeholder="tr('No value option for current filter')"
        />
      </div>
    </div>

    <ui-button
      v-if="removable && !disabled"
      variant="ghost"
      size="icon"
      :aria-label="tr('Remove')"
      @click="$emit('remove')"
    >
      <trash-2-icon />
    </ui-button>
  </div>
</template>

<script>
import { ChevronsUpDown, Trash2, TriangleAlert } from '@lucide/vue';

import AdAccount from '@/entities/AdAccount';
import { ConditionValidatable } from '@/mixins/RuleForm/validatable.js';
import DateTimeInput from '@/ui/components/DateTimeInput.vue';
import MoneyInput from '@/ui/components/MoneyInput.vue';
import NumberInput from '@/ui/components/NumberInput.vue';
import PercentageInput from '@/ui/components/PercentageInput.vue';
import SelectField from '@/ui/components/SelectField.vue';
import { ucfirst } from '@/utils/string';

export default {
  name: 'ConnectionRow',
  components: {
    DateTimeInput,
    MoneyInput,
    NumberInput,
    PercentageInput,
    SelectField,
    ChevronsUpDownIcon: ChevronsUpDown,
    Trash2Icon: Trash2,
    TriangleAlertIcon: TriangleAlert,
  },
  mixins: [ ConditionValidatable ],
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    action: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    platforms: {
      type: Array,
      required: false,
      default: () => [ 'facebook', 'instagram' ],
    },
    removable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    disablePostMetrics: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    adAccount: {
      type: [ AdAccount, null ],
      required: false,
      default: () => null,
    },
    loadingSubfields: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    subfieldsMap: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data: () => ({
    changingManually: false,
    comboSubfield: null,
    fieldMenuOpen: false,
    fieldSearch: '',
  }),
  computed: {
    fieldList() {
      const items = JSON.parse(JSON.stringify(this.$store.getters['app/fields']));

      items.forEach(field => {
        const allowedByPlatform = this.platforms.every(elem => field.platforms.includes(elem));
        const allowedByAction = field.actions.includes(this.action);
        const disabledMetric = field.machine_name.startsWith('post.metric.') && this.disablePostMetrics;

        field.disabled = !allowedByPlatform || !allowedByAction || disabledMetric;

        if (field.disabled) {
          let disabledReason = null;

          if (!allowedByAction) {
            disabledReason = this.tr('This value is not available for the :action automation', { ':action': this.tr(this.action) });
          }
          else if (!allowedByPlatform) {
            const platform = this.platforms.filter(i => !field.platforms.includes(i))[0];
            disabledReason = this.tr('This value is not available for the :platform platform', { ':platform': ucfirst(platform) });
          }
          else if (disabledMetric) {
            disabledReason = this.tr('The information is not available for the posts of the selected data source');
          }

          if (disabledReason !== null) {
            field.tooltip = disabledReason;
          }
        }
      });

      items.sort((a, b) => {
        if (!a.disabled && b.disabled) {
          return -1;
        }
        else if (a.disabled && !b.disabled) {
          return 1;
        }

        return 0;
      });

      return items;
    },
    selectedField() {
      return this.fieldList.find(i => i.machine_name === this.modelValue.field);
    },
    datePeriods() {
      return this.selectedField.date_periods;
    },
    operatorOptions() {
      if (!this.selectedField) {
        return [];
      }

      const items = this.$store.getters['app/fieldComparisons'];
      const comparisons = items.filter(operator =>
        operator.data_types.includes(this.selectedField.data_type)
        || (
          (this.isDataTypeInt || this.isDataTypeFloat || this.isDataTypeMoney)
          && operator.dynamic_num
        ));

      return comparisons;
    },
    selectedOperator() {
      if (!this.modelValue.comparison) {
        return null;
      }

      return this.$store.getters['app/fieldComparisons'].find(i => i.machine_name === this.modelValue.comparison);
    },
    dynamicOperatorValueOptions() {
      return this.selectedOperator ? this.selectedOperator.dynamic_value_options : [];
    },
    dynamicOperatorAreas() {
      if (!this.modelValue.field || !this.selectedOperator || !this.selectedOperator.areas) {
        return null;
      }

      const fieldType = this.modelValue.field.split('.')[0];

      if (!(fieldType in this.selectedOperator.areas)) {
        return null;
      }

      return this.selectedOperator.areas[fieldType];
    },
    dataType() {
      return this.selectedField ? this.selectedField.data_type : null;
    },
    isDataTypeString() {
      return this.dataType === 'string';
    },
    isDataTypeInt() {
      return this.dataType === 'int';
    },
    isDataTypeFloat() {
      return this.dataType === 'float';
    },
    isDataTypeMoney() {
      return this.dataType === 'money';
    },
    isDataTypePercentage() {
      return this.dataType === 'percentage';
    },
    isDataTypeLanguage() {
      return this.dataType === 'language';
    },
    isDataTypeDateTime() {
      return this.dataType === 'datetime';
    },
    isDynamicOperator() {
      return this.selectedOperator && this.selectedOperator.dynamic_num;
    },
    showSubfields() {
      return this.selectedField && this.selectedField.dynamic_subfields;
    },
    showDatePeriods() {
      return this.selectedField
        && this.selectedField.date_periods
        && this.selectedField.date_periods.length !== 0;
    },
    disabledComparisonSelect() {
      return this.operatorOptions.length === 0 || (this.showSubfields && this.modelValue.subfield === null);
    },
    disabledValueInput() {
      return this.disabledComparisonSelect || !this.modelValue.comparison;
    },
    languageOptions() {
      return this.$store.getters['app/languagesISO'];
    },
    currentSubfields() {
      return this.modelValue.field && this.subfieldsMap[this.modelValue.field]
        ? this.subfieldsMap[this.modelValue.field]
        : [];
    },
    subfieldSearchOptions() {
      const options = [];

      for (const parent of this.fieldList) {
        if (!parent.dynamic_subfields || parent.disabled) {
          continue;
        }

        const subs = this.subfieldsMap[parent.machine_name] || [];
        for (const sub of subs) {
          options.push({
            machine_name: parent.machine_name, // reduce → value.field = the parent
            label: sub.label, // rendered through tr() and matched against
            isSubfieldOption: true,
            subfieldMachineName: sub.machine_name,
            parentLabel: parent.label,
            // Inherited from the parent so the row stays consistent if rendered.
            data_type: parent.data_type,
            platforms: parent.platforms,
            actions: parent.actions,
            date_periods: parent.date_periods,
            disabled: false,
          });
        }
      }

      return options;
    },
    visibleFieldOptions() {
      return this.fieldSearch.trim() === ''
        ? this.fieldList
        : this.fieldSearchFilter(this.fieldList, this.fieldSearch.trim());
    },
    fieldSearchFilter() {
      return (options, search) => {
        const needle = search.toLocaleLowerCase();
        const match = label => this.tr(label).toLocaleLowerCase().indexOf(needle) > -1;

        const mainMatches = options.filter(option => match(option.label));
        const subMatches = this.subfieldSearchOptions.filter(option => match(option.label));

        return [ ...mainMatches, ...subMatches ];
      };
    },
  },
  watch: {
    dataType() {
      if (!this.changingManually) {
        return;
      }

      this.modelValue.value = '';
      this.modelValue.comparison = '';

      if (this.isDataTypeDateTime) {
        this.modelValue.value = new Date();
      }
    },
    selectedField: {
      immediate: true,
      handler(field) {
        if (!field) {
          return;
        }

        if (this.comboSubfield) {
          this.modelValue.subfield = this.comboSubfield;
          this.comboSubfield = null;
          return;
        }

        if (this.changingManually) {
          this.modelValue.subfield = null;
        }
      },
    },
    'modelValue.field': {
      immediate: true,
      handler() {
        if (
          this.showSubfields
          && this.adAccount
          && !this.subfieldsMap[this.modelValue.field]
          && !this.$store.getters['dynamicSubfields/isLoading'](this.adAccount.id)
        ) {
          this.$store.dispatch('dynamicSubfields/fetch', { adAccountId: this.adAccount.id })
            .catch(() => { /* the owning ConditionGroup surfaces the error toast */ });
        }
      },
    },
    showDatePeriods: {
      immediate: true,
      handler(val) {
        if (val) {
          if (!this.modelValue.date_period) {
            this.modelValue.date_period = 'last_30d';
          }
        }
        else {
          this.modelValue.date_period = null;
        }
      },
    },
    isDynamicOperator(val, oldVal) {
      if (val && !oldVal) {
        this.modelValue.settings.dynamic_operator_direction = this.dynamicOperatorValueOptions[0].machine_name;
        this.modelValue.settings.dynamic_operator_area = this.dynamicOperatorAreas[0].machine_name;
      }
      else if (!val && oldVal) {
        this.modelValue.settings.dynamic_operator_direction = null;
        this.modelValue.settings.dynamic_operator_area = null;
      }
    },
    dynamicOperatorAreas(val, oldVal) {
      if (val && oldVal) {
        this.modelValue.settings.dynamic_operator_area = this.dynamicOperatorAreas[0].machine_name;
      }
    },
    'modelValue.value'(val) {
      if (this.isDynamicOperator) {
        if (val < 1) {
          this.modelValue.value = 1;
        }
        else if (val > 100) {
          this.modelValue.value = 100;
        }
      }
    },
  },
  methods: {
    onFieldMenuOpen(open) {
      this.fieldMenuOpen = open;
      if (!open) {
        this.fieldSearch = '';
      }
    },
    // A subfield option picks its parent field and the subfield in one step.
    selectField(field) {
      if (field.disabled) {
        return;
      }

      this.changingManually = true;
      this.onFieldMenuOpen(false);

      if (field.isSubfieldOption) {
        if (this.modelValue.field === field.machine_name) {
          this.modelValue.subfield = field.subfieldMachineName;
          this.$nextTick(() => this.changingManually = false);
          return;
        }

        this.comboSubfield = field.subfieldMachineName;
      }

      this.modelValue.field = field.machine_name;
      this.$nextTick(() => this.changingManually = false);
    },
    validate() {
      if (!this.modelValue.field) {
        this.error(this.tr('All fields must be filled in the filter conditions.'));
        return false;
      }

      const field = this.fieldList.find(i => i.machine_name === this.modelValue.field);
      if (field.disabled) {
        this.error(this.tr('A value has been selected in the filter conditions that is not available.'));
        return false;
      }

      return true;
    },
    setLanguageComparison() {
      this.modelValue.comparison = 'eq';
    },
  },
};
</script>
